import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import AdministrationPermissionsLoadState from "legal-entities/administrators/_views/administration-permissions-load-state";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import { useCallback, useEffect } from "react";
import { useSelector } from "react-redux";
import { Outlet } from "react-router";
import { useSession } from "users/__session/session-context";

/**
 * Every `/permissions` route, wrapped once.
 *
 * The shell loads the session's own grants, and shows the load or its failure, so that no route below
 * it answers "not authorized" from a set that has not arrived. It does not gate: the routes ask their
 * own question once the grants are here, because they ask different ones — a colleague's page needs
 * Member Permissions and your own page needs only membership (`docs/design/permissions/
 * PERMISSIONS_UI_DESIGN.md`, "Routes and gates").
 */
export default function PermissionsShell() {
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const sessionPermissions = useSelector(getSessionPermissions);
  const loading = useSelector((state: RootState) => state.permissions.session.loading);
  const error = useSelector((state: RootState) => state.permissions.session.error);

  useEffect(() => {
    if (!sessionPermissions && !error && !loading) {
      dispatch(populateSessionPermissions({ session }));
    }
  }, [dispatch, session, sessionPermissions, error, loading]);

  const retry = useCallback(() => {
    if (!loading) dispatch(populateSessionPermissions({ session }));
  }, [dispatch, session, loading]);

  if (!sessionPermissions) return <AdministrationPermissionsLoadState error={error} onRetry={retry} />;
  return <Outlet />;
}
